import { motion } from "framer-motion";
import styles from "./FireBackground.module.css";
import type { BetSide } from "../store/game";

type Direction = "up" | "down" | "flat";
type Props = {
  direction: Direction;
  activeSide: BetSide | null;
  onTap: (side: BetSide) => void;
  disabled?: boolean;
};

// Dynamics: Active flame grows huge, inactive flame shrinks down
const T = {
  active: { scaleY: 1.15, scaleX: 1.1, opacity: 1, y: "0%" },
  lead:   { scaleY: 1.05, scaleX: 1.05, opacity: 1, y: "0%" },
  neutral:{ scaleY: 0.9, scaleX: 1, opacity: 0.9, y: "5%" },
  lag:    { scaleY: 0.65, scaleX: 0.9, opacity: 0.7, y: "15%" },
  back:   { scaleY: 0.55, scaleX: 0.85, opacity: 0.6, y: "20%" },
};

function getIntensity(variant: "blue"|"red", d: Direction, a: BetSide|null) {
  const isUp = variant === "red"; 
  if (a !== null) {
    if ((isUp && a === "up") || (!isUp && a === "down")) return "active";
    return "back"; 
  }
  if (d === "flat") return "neutral";
  if ((isUp && d === "up") || (!isUp && d === "down")) return "lead";
  return "lag";
}

export function FireBackground({ direction, activeSide, onTap, disabled }: Props) {
  const bI = getIntensity("blue", direction, activeSide);
  const rI = getIntensity("red", direction, activeSide);

  return (
    <div className={styles.root}>
      {/* LEFT HALF */}
      <button type="button" className={styles.zone} onClick={() => onTap("down")} disabled={disabled}>
        <Flame variant="blue" state={T[bI as keyof typeof T]} />
      </button>

      {/* RIGHT HALF */}
      <button type="button" className={styles.zone} onClick={() => onTap("up")} disabled={disabled}>
        <Flame variant="red" state={T[rI as keyof typeof T]} />
      </button>
    </div>
  );
}

// Beautiful smooth S-curve flame paths 
// Designed to look like the reference: wide base, pointed tip, smooth S-bends
const PATHS = {
  // Broad outer layer
  out: "M 10 300 C -20 220, 60 160, 40 100 C 30 70, 40 40, 50 10 C 60 40, 70 70, 60 100 C 40 160, 120 220, 90 300 Z",
  // Mid layer
  mid: "M 25 300 C 0 230, 60 170, 45 110 C 38 80, 43 45, 50 18 C 57 45, 62 80, 55 110 C 40 170, 100 230, 75 300 Z",
  // Inner hot core
  in:  "M 38 300 C 20 240, 60 180, 48 120 C 43 90, 46 60, 50 25 C 54 60, 57 90, 52 120 C 40 180, 80 240, 62 300 Z",
};

// Mirrored paths for the right side so they curve into the center symmetrically
function mirrorPath(path: string) {
  // Flip X axis around 50
  return path.replace(/([-\d]+)(?=\s+[-\d]+)/g, (match, xBase, offset, string) => {
    // Check if what follows is a Y coordinate or an SVG command letter
    // Actually, simple regex to flip X around 50 for pure C M Z commands
    return (100 - parseFloat(match)).toString();
  });
}

const RED_PATHS = {
  out: "M 90 300 C 120 220, 40 160, 60 100 C 70 70, 60 40, 50 10 C 40 40, 30 70, 40 100 C 60 160, -20 220, 10 300 Z",
  mid: "M 75 300 C 100 230, 40 170, 55 110 C 62 80, 57 45, 50 18 C 43 45, 38 80, 45 110 C 60 170, 0 230, 25 300 Z",
  in:  "M 62 300 C 80 240, 40 180, 52 120 C 57 90, 54 60, 50 25 C 46 60, 43 90, 48 120 C 40 180, 20 240, 38 300 Z",
};

function Flame({ variant, state }: { variant: "blue"|"red"; state: any }) {
  const isBlue = variant === "blue";
  
  const containerClass = `${styles.flameContainer} ${isBlue ? styles.containerLeft : styles.containerRight}`;
  const glowClass = isBlue ? styles.glowBlue : styles.glowRed;
  const activeClass = state.scaleY > 1.1 ? (isBlue ? styles.glowBlueActive : styles.glowRedActive) : "";
  const className = `${styles.flameSvg} ${glowClass} ${activeClass}`;

  // Organic flame swaying animations
  const swayVariants = {
    animate: {
      rotate: isBlue ? [-3, 2, -3] : [3, -2, 3],
      skewX: isBlue ? [-2, 3, -2] : [2, -3, 2],
      transition: { duration: 3, repeat: Infinity, ease: "easeInOut" } as any
    }
  };
  const swayMid = {
    animate: {
      rotate: isBlue ? [2, -1, 2] : [-2, 1, -2],
      transition: { duration: 2.5, repeat: Infinity, ease: "easeInOut" } as any
    }
  };
  const swayIn = {
    animate: {
      skewX: isBlue ? [1, -2, 1] : [-1, 2, -1],
      transition: { duration: 2, repeat: Infinity, ease: "easeInOut" } as any
    }
  };

  const p = isBlue ? PATHS : RED_PATHS;

  return (
    <motion.div 
      className={containerClass}
      animate={state}
      transition={{ type: "spring", stiffness: 60, damping: 15 }}
    >
      <svg className={className} viewBox="0 0 100 300" preserveAspectRatio="none" aria-hidden>
        <defs>
          {isBlue ? (
            <>
              <linearGradient id="blue_out" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor="#0033aa" stopOpacity="1"/>
                <stop offset="0.5" stopColor="#0077dd" stopOpacity="0.85"/>
                <stop offset="1" stopColor="#00aaff" stopOpacity="0"/>
              </linearGradient>
              <linearGradient id="blue_mid" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor="#0066ee" stopOpacity="1"/>
                <stop offset="0.6" stopColor="#33aaff" stopOpacity="0.9"/>
                <stop offset="1" stopColor="#88ddff" stopOpacity="0"/>
              </linearGradient>
              <linearGradient id="blue_in" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor="#33bbff" stopOpacity="1"/>
                <stop offset="0.5" stopColor="#88ddff" stopOpacity="0.95"/>
                <stop offset="1" stopColor="#ffffff" stopOpacity="0.2"/>
              </linearGradient>
            </>
          ) : (
            <>
              <linearGradient id="red_out" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor="#bb2200" stopOpacity="1"/>
                <stop offset="0.5" stopColor="#ee4400" stopOpacity="0.85"/>
                <stop offset="1" stopColor="#ff8800" stopOpacity="0"/>
              </linearGradient>
              <linearGradient id="red_mid" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor="#ee4400" stopOpacity="1"/>
                <stop offset="0.6" stopColor="#ff7700" stopOpacity="0.9"/>
                <stop offset="1" stopColor="#ffbb00" stopOpacity="0"/>
              </linearGradient>
              <linearGradient id="red_in" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor="#ff8800" stopOpacity="1"/>
                <stop offset="0.5" stopColor="#ffcc00" stopOpacity="0.95"/>
                <stop offset="1" stopColor="#ffffff" stopOpacity="0.2"/>
              </linearGradient>
            </>
          )}
        </defs>

        {/* OUTER LAYER */}
        <motion.g variants={swayVariants} animate="animate" style={{ originX: "50%", originY: "100%" }}>
          <path fill={`url(#${isBlue ? 'blue_out' : 'red_out'})`} d={p.out} />
        </motion.g>

        {/* MIDDLE LAYER */}
        <motion.g variants={swayMid} animate="animate" style={{ originX: "50%", originY: "100%" }}>
          <path fill={`url(#${isBlue ? 'blue_mid' : 'red_mid'})`} d={p.mid} />
        </motion.g>

        {/* INNER LAYER */}
        <motion.g variants={swayIn} animate="animate" style={{ originX: "50%", originY: "100%" }}>
          <path fill={`url(#${isBlue ? 'blue_in' : 'red_in'})`} d={p.in} />
        </motion.g>
      </svg>
    </motion.div>
  );
}
